import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import BreadCrumb from 'src/Components/GlobalComponents/BreadCrumb';
import ConfirmationModal from 'src/Components/GlobalComponents/ConfirmationModal';
import SearchFilterInput from 'src/Components/GlobalComponents/SearchFilterInput';
import SelectDropdown from 'src/Components/GlobalComponents/SelectDropdown';
import TableWrapper from 'src/Components/GlobalComponents/TableWrapper';
import UpsertPharmaInventory from 'src/Components/PharmacyComponents/Admin/UpsertPharmaInventory';
import httpRequest from 'src/helpers/httpRequest';
import { IMainCategory, IPharmaBrand, IPharmaInventory, IqueryParams } from 'src/types/GlobalInterfaces';
import { IPaginated } from 'src/types/paginated';
import qs from "qs";
import editIcon from 'src/assets/images/edit-icon.png';
import deleteIcon from 'src/assets/images/delete-icon.png';
import ToggleRadioBtn from 'src/Components/ToggleRadioBtn';
import { useSelector } from 'react-redux';
import { RootState } from '@store/store';
import { canWrite } from "src/helpers/helperFunctions";
import CustomWebsiteDropdown from 'src/Components/GlobalComponents/Widget/CustomWebsiteDropdown';
import { Option } from 'react-multi-select-component';
import { CURRENCY } from 'src/lib/constans';


interface IResponce {
    documents: IPharmaInventory[];
    paginated: IPaginated;
};

const PharmaInventory: React.FC = () => {
    const { userDetails } = useSelector((store: RootState) => store.appReducer);
    const hasWriteAccess = canWrite(userDetails, "pharmaInventory", false);

    const [statusLoader, setStatusLoader] = useState<string | null>(null);
    const [allBrands, setAllBrands] = useState<{ label: string; value: string; }[]>([]);
    const [allCategories, setAllCategories] = useState<{ label: string; value: string; }[]>([]);
    const [isUpsert, setIsUpsert] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<IPharmaInventory | null>(null);
    const [isLoader, setIsLoader] = useState<boolean>(false);
    const [deleteLoader, setDeleteLoader] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<string>("");
    const [allProducts, setallProducts] = useState<IPharmaInventory[]>([]); // State to store the list of products
    const [paginated, setPaginated] = useState<IPaginated>({
        currentPage: 1,
        totalPages: 1,
    });
    const [queryParams, setQueryParams] = useState<IqueryParams>({
        page: 1,
        limit: 10,
        searchProduct: "",
        bool: false,
        searchBrand: "", // depend on backend
        searchCategory: "", // depend on backend
    });

    // Function change search text for api query params
    const handleChangeText = (txt: string) => {
        setQueryParams(prev => ({
            ...prev,
            page: 1,
            searchProduct: txt
        }));
    };

    // Function to close the delete confirmation modal
    const deleteModalClose = () => {
        setDeleteModal(false);
        setDeleteId("");
    };

    const handleUpdateList = () => {
        setQueryParams(prev => ({
            ...prev,
            bool: !prev.bool,
        }));
        setIsUpsert(false);
        setIsEdit(null);
    };

    // Function to handle page change in pagination
    const handlePageChange = (e: number) => {
        setQueryParams(prev => ({
            ...prev,
            page: e
        }));
    };

    // Function to set the component to edit mode and populate it with brand data
    const handleEdit = (obj: IPharmaInventory) => {
        if (!hasWriteAccess) {
            return
        }
        setIsUpsert(true);
        setIsEdit({
            ...obj,
            frequentlyBoughtTogether: obj?.frequentlyBoughtDetails.map((item : any) => item?._id),
            promotionalTagName: obj?.promotionalTagDetails || {}
        });
    };

    // Function to show the delete confirmation modal and set the ID of the brand to be deleted
    const handleDelete = (id: string) => {
        if (!hasWriteAccess) {
            return
        }
        setDeleteModal(true);
        setDeleteId(id);
    };

    // API_CALL: Function to delete a product by its ID
    const deleteRequests = async () => {

        setDeleteLoader(true);
        const { res, err } = await httpRequest<any>({ method: "delete", path: `/pharma-inventory/delete/${deleteId}`, params: {} });
        if (res) {
            toast("Inventory deleted successfully.", { type: 'success' });
            setQueryParams(prev => ({
                ...prev,
                bool: !prev.bool,
                page: allProducts?.length > 1 ? prev?.page : prev?.page > 1 ? prev?.page - 1 : prev?.page
            }));
            deleteModalClose();
        } else {
            toast(err, { type: 'error' });
        }
        setDeleteLoader(false);
    };

    const printMainCategoryName = (id: string): string => {
        return allCategories?.find(v => v?.value === id)?.label || "-";
    };

    const handleChangeFilter = (key: string, e: { value: string; label: string }[] | null) => {
        setQueryParams(prev => ({
            ...prev,
            page: 1,
            [key]: e ? e[0]?.value || "" : ""
        }));
    };

    const handleStatusToogle = async (id: string) => {
        if (!hasWriteAccess) {
            return
        }
        setStatusLoader(id);
        const { res, err } = await httpRequest<{ message: string; status: boolean }>({
            method: "put",
            path: `/pharma-inventory/updateStatus/${id}`,
            params: {}
        });
        if (res) {
            const { message = "", status = false } = res;
            toast(message, { type: "success" });
            setallProducts(prev =>
                prev.map(v =>
                    v?._id === id
                        ? { ...v, status: status }
                        : v
                ));
        } else {
            toast(err, { type: "error" });
        }
        setStatusLoader(null);
    };

    const fetchAllBrands = async () => {
        const { res, err } = await httpRequest<{ documents: IPharmaBrand[] }>({ path: "/brand/getAllBrands" });
        if (res) {
            const arr = res?.documents?.map(v => ({ label: v?.brandName, value: v?._id })) || [];
            setAllBrands([{ value: "", label: "All" }, ...arr]);
        } else {
            toast(err, { type: "error" });
        }
    };
    const fetchAllCategories = async () => {
        const { res, err } = await httpRequest<{ documents: IMainCategory[] }>({ path: "/main-category/getAllMainCategory" });
        if (res) {
            const arr = res?.documents?.map(v => ({ label: v?.categoryName, value: v?._id })) || [];
            setAllCategories([{ value: "", label: "All" }, ...arr]);
        } else {
            toast(err, { type: "error" });
        }
    };

    const fetchAllProducts = async () => {
        setIsLoader(true);
        const { bool, ...remainingParams } = queryParams;
        const { res, err } = await httpRequest<IResponce>({ path: `/pharma-inventory/getAll?${qs.stringify(remainingParams)}` });
        if (res) {
            const { documents = [], paginated = { currentPage: 1, totalPages: 1 } } = res;
            setallProducts(documents);
            setPaginated(paginated);
        } else {
            toast(err, { type: "error" });
        }
        setIsLoader(false);
    };

    useEffect(() => {
        fetchAllBrands();
        fetchAllCategories();
    }, []);

    useEffect(() => {
        fetchAllProducts();
    }, [queryParams]);

    return (
        <>
            <ConfirmationModal
                show={deleteModal}
                onClose={deleteModalClose}
                title="Are you sure you want to delete?"
                leftBtnText="No"
                rightBtnText="Yes"
                onConfirm={deleteRequests}
                loader={deleteLoader}
            />
            <BreadCrumb
                current="Pharma Inventory"
                previous="dashboard"
                navigateUrl="/admin"
                btnText={"Add New Product"}
                btnShow={true}
                onPress={() => {
                    if (!hasWriteAccess) {
                        return
                    }
                    setIsEdit(null); setIsUpsert(prev => !prev)
                }}
                isUpsert={isUpsert}
                btnClassName={`${!hasWriteAccess ?  'cursor-not-allowed':""}`}
                BtnId="add-pharma-inventory-btn"
            />
            <div className="table-container">
                {isUpsert ?
                    <UpsertPharmaInventory
                        onSuccess={handleUpdateList}
                        isEdit={isEdit}
                    />
                    // <></>    
                    :
                    <>
                        <div className='mb-3 flex items-center justify-between'>
                            <div className="search__dropdown flex flex-row gap-2">
                                <div>
                                    {/* <SelectDropdown
                                        extraClass="m-0 min-w-[200px] p-0"
                                        loader={false}
                                        options={allBrands}
                                        isMulti={false}
                                        data={allBrands.filter(v => v?.value === queryParams?.searchBrand)}
                                        setData={(e) => handleChangeFilter("searchBrand", e)}
                                        checkboxShow={false}
                                        isLabel={true}
                                        clearIcon={queryParams?.searchBrand ? true : false}
                                        labelText="Filter By Brand"
                                    /> */}

                                    <CustomWebsiteDropdown
                                        options={allBrands}
                                        data={allBrands.filter(v => v?.value === queryParams?.searchBrand)}
                                        setData={(e) => handleChangeFilter("searchBrand", e)}
                                        labelText="Filter By Brand"
                                        isRequired={true}
                                        dropdownMainId="brand-dropdown"
                                        clearId="brand-clear"
                                        optionId={(v : Option , index) => `brand-option-${index + 1}`}
                                        loading={false}
                                        extraClass="m-0 min-w-[200px] p-0"
                                    />
                                </div>
                                <div>
                                    {/* <SelectDropdown
                                        extraClass="m-0 min-w-[200px] p-0"
                                        loader={false}
                                        options={allCategories}
                                        isMulti={false}
                                        data={allCategories.filter(v => v?.value === queryParams?.searchCategory)}
                                        setData={(e) => handleChangeFilter("searchCategory", e)}
                                        checkboxShow={false}
                                        isLabel={true}
                                        clearIcon={queryParams?.searchCategory ? true : false}
                                        labelText="Filter By Category"
                                    /> */}

                                    <CustomWebsiteDropdown
                                        options={allCategories}
                                        data={allCategories.filter(v => v?.value === queryParams?.searchCategory)}
                                        setData={(e) => handleChangeFilter("searchCategory", e)}
                                        labelText="Filter By Category"
                                        isRequired={true}
                                        dropdownMainId="category-dropdown"
                                        clearId="category-clear"
                                        optionId={(v : Option , index) => `category-option-${index + 1}`}
                                        loading={false}
                                        extraClass="m-0 min-w-[200px] p-0"
                                    />
                                </div>
                            </div>
                            <SearchFilterInput
                                onChangeText={handleChangeText}
                                inputExtraStyle={{ marginBottom: 0 }}
                                searchId="search-input"
                            />
                        </div>
                        <TableWrapper
                            isLoading={isLoader}
                            thead={["S.No", "Brand Name", "Product Name", "Category", "Price", "Sale Price", "Quantity", "Status", "Action"]}
                            page={paginated?.currentPage || 1}
                            total={paginated?.totalPages || 0}
                            columnWidth="10% 14% 15% 13% 8% 11% 10% 9% 10%"
                            onChange={handlePageChange}
                        >
                            <div className='tbody-content-height'>
                                {allProducts.map((item, index: number) => (
                                    <ul key={index} className='font-size-16px tbody-light-table font-Poppins-Regular grid' style={{ gridTemplateColumns: "10% 14% 15% 13% 8% 11% 10% 9% 10%" }}>
                                        <li>{(paginated?.currentPage - 1) * queryParams.limit + (index + 1)}</li>
                                        <li>{item?.brandName || "-"}</li>
                                        <li>{item?.productName || "-"}</li>
                                        <li>{printMainCategoryName(item?.mainCategoryId)}</li>
                                        <li>{CURRENCY} {item?.regularPrice || "-"}</li>
                                        <li>{item?.salePrice && parseInt(item?.salePrice?.toString() || "0") > 0 ? CURRENCY + " " + item?.salePrice : "N/A"}</li>
                                        <li>{item?.quantity ?? "-"}</li>
                                        <li className='justify-center' style={{ justifyContent: 'center', paddingLeft: 0, }}>
                                            <div className={`${!hasWriteAccess ? 'read_access' : ""}`}>
                                                <ToggleRadioBtn
                                                    isToggled={item?.status}
                                                    handleToggle={() => handleStatusToogle(item._id)}
                                                    loader={statusLoader === item?._id ? true : false}
                                                    id={`status-toggle-btn-${index + 1}`}
                                                />
                                            </div>
                                        </li>
                                        <li className={`action-last-colum `}>
                                            <img id={`edit-pharma-inventory-btn-${index + 1}`} onClick={() => handleEdit(item)} src={editIcon} alt="edit" className={`${!hasWriteAccess ? 'read_access' : ""}`} />
                                            <img id={`delete-pharma-inventory-btn-${index + 1}`} onClick={() => handleDelete(item?._id)} src={deleteIcon} alt="delete" className={`${!hasWriteAccess ? 'read_access' : ""}`} />
                                        </li>
                                    </ul>
                                ))}
                            </div>
                        </TableWrapper>
                    </>
                }
            </div>
        </>
    );
};

export default PharmaInventory;